import { Tr } from "@chakra-ui/react";
import { EnvironmentalType } from "../../../enum/EnvironmentalType";
import { Row, ThresholdItem, Tresholds } from "../types";
import { TableCell } from "./TableCell";

interface TableRowProps {
  row: Row[];
  index: number;
  thresholds: Tresholds;
  hasScrolled: boolean;
  verifyIfColumnHided: (type: string | null) => boolean;
  isSubColumnHided: (type: string | null) => boolean;
  verifyColumnType: (
    key: string
  ) =>
    | EnvironmentalType.WAVE_SEA
    | EnvironmentalType.WAVE_SWELL
    | EnvironmentalType.WATER_FLOW
    | EnvironmentalType.WEATHER
    | EnvironmentalType.WAVE
    | EnvironmentalType.WATER_PROPERTIES
    | null;
  getColorForValue: (value: number | null, threshold: ThresholdItem) => string;
}

interface FormattedRow {
  colorForValue: string;
  isDate: boolean;
  cellIndex: number;
  value: string | number;
  after: {
    [key: string]: string;
  };
  key: string;
}

export function TableRow({
  row,
  index,
  thresholds,
  getColorForValue,
  verifyIfColumnHided,
  isSubColumnHided,
  verifyColumnType,
  hasScrolled,
}: TableRowProps) {
  const getIsHidden = (key: string) => {
    const columnType = verifyColumnType(key);

    return verifyIfColumnHided(columnType);
  };

  const formattedRow = row
    .map((cell, index) => {
      const threshold = thresholds[cell.key];

      const isHidedColumn = getIsHidden(cell.key);
      const isHidedSubColumn = isSubColumnHided(cell.key);

      if (isHidedColumn || isHidedSubColumn) {
        return false;
      }

      const value = cell.value as number | null;

      const colorForValue = getColorForValue(value, threshold);

      const isDate = cell.key === "dates";

      const after =
        isDate && hasScrolled
          ? {
              content: '""',
              position: "absolute",
              top: 0,
              right: 0,
              width: "0.5px",
              height: "100%",
              background: "black",
            }
          : {};

      return {
        colorForValue,
        isDate,
        cellIndex: index,
        value: cell.value ?? "-",
        after,
        key: cell.key,
      };
    })
    .filter((data) => !!data) as FormattedRow[];

  const getIfRowIsEmpty = () => {
    const copy = [...formattedRow];
    copy.shift();

    return copy.every((row) => row.value === "-");
  };

  const getColorForTime = (values: FormattedRow[]) => {
    if (!values.length || values.length < 1) {
      return "inherit";
    }

    if (values.some((item) => item.colorForValue === "red.300")) {
      return "red.300";
    }

    if (
      values.some((item) => item.colorForValue === "yellow.300") &&
      values.some((item) => item.colorForValue === "green.300")
    ) {
      return "yellow.300";
    }

    if (values.every((item) => item.colorForValue === "yellow.300")) {
      return "yellow.300";
    }

    if (values.every((item) => item.colorForValue === "green.300")) {
      return "green.300";
    }

    return "inherit";
  };

  const isRowEmpty = getIfRowIsEmpty();
  const dateColor = getColorForTime(
    formattedRow.filter((row) => !row.isDate && row.colorForValue !== "inherit")
  );

  return (
    <Tr key={index} bg={index % 2 === 0 ? "#c0d5e5" : "white"}>
      {!isRowEmpty &&
        formattedRow.map((row) => {
          return (
            <TableCell
              key={row.cellIndex}
              isDate={row.isDate}
              value={row.value}
              colorForValue={row.colorForValue}
              dateColor={dateColor}
              after={row.after}
              origin={row.key}
            />
          );
        })}
    </Tr>
  );
}
